import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import debounce from "lodash-es/debounce";
import memoize from "lodash-es/memoize";
import { parse, stringify } from "query-string";
import React from "react";

import SearchResults from "@/mk2/apps/blogs/components/SearchResults";
import GroupsGrid from "@/mk2/apps/groups/components/GroupsGrid";
import { groupsListFromCategoryUrl, groupsListUrl } from "@/mk2/apps/groups/urls";
import { ClearableInput } from "@/mk2/components/ClearableInput";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Interpolate } from "@/mk2/components/Interpolate";
import { ListItem } from "@/mk2/components/ListItem";
import { ListItemIcon } from "@/mk2/components/ListItemIcon";
import { url } from "@/mk2/helpers/urls";
import { isSuccess } from "@/mk2/reducers/loadingState";
import {
    CATEGORY,
    GROUP_YOU_ARE_NOT_A_MEMBER,
    LIST_OF_GROUPS_NEWEST,
    LIST_OF_GROUPS_POPULAR,
    LIST_OF_GROUPS_SEARCH_GROUPS_PLACEHOLDER,
    LIST_OF_GROUPS_SEARCH_RESULT_category_count,
    LIST_OF_GROUPS_SEARCH_RESULT_query_category_count,
    LIST_OF_GROUPS_SEARCH_RESULT_query_count,
} from "@/mk/autogenerated/translations/ListOfGroups.9ad38a29e8231adbe897e81df8a046ea";

import styles from "./ListOfGroups.mscss";
import { Props } from "./ListOfGroupsPage";

interface State {
    query: string;
}

export class ListOfGroups extends React.Component<Props, State> {
    private searchRedirectDebounced: () => void;

    private onCategorySelect = memoize(
        (slug: string) => (event: React.MouseEvent<HTMLDivElement>) => {
            const {
                location: { search },
                onCategorySelectClose,
            } = this.props;
            this.props.routeNavigate(
                {
                    pathname: slug
                        ? url(groupsListFromCategoryUrl, { categorySlug: slug })
                        : groupsListUrl,
                    search,
                },
                { replace: true },
            );
            onCategorySelectClose();
        },
    );

    public constructor(props) {
        super(props);

        this.state = {
            query: props.query || "",
        };

        this.searchRedirectDebounced = debounce(this.searchRedirect, 500);
    }

    public componentDidMount() {
        const query = parse(this.props.location.search) || {};

        if (query.notMember === "true") {
            this.props.onErrorToast(GROUP_YOU_ARE_NOT_A_MEMBER);

            // remove 'notMember' parameter to not show the toast again on page reload
            this.props.routeNavigate(groupsListUrl, { replace: true });
        }
    }

    public render() {
        const {
            after,
            askedForMembership,
            categories,
            categoriesLoadingState,
            category,
            count,
            groups,
            hasNext,
            hasPrevious,
            isSelectingCategory,
            joining,
            loadingState,
            location,
            routeParams,
            memberships,
            isMobile,
            newGroups,
            numGroupsInCategory,
            onCategorySelectOpen,
            query,
            requestUser,
        } = this.props;

        if (isSelectingCategory) {
            return (
                <>
                    {categories.map((item, index) => (
                        <ListItem
                            className={styles.ListOfGroups__category}
                            key={`category-${item.id}`}
                            icon={<ListItemIcon lazy photo={item.coverPhoto} />}
                            isLast={index === categories.length - 1}
                            onClick={this.onCategorySelect(item.slug)}
                        >
                            {item.name + " "}
                            <span className={styles.ListOfGroups__category__number}>
                                ({numGroupsInCategory[item.id] || 0})
                            </span>
                        </ListItem>
                    ))}
                </>
            );
        }

        return (
            <div className={styles.ListOfGroups}>
                <div className={styles.ListOfGroups__filters}>
                    <ClearableInput
                        icon={
                            <label htmlFor="search">
                                <FontAwesomeIcon icon={faSearch} />
                            </label>
                        }
                        hasValue={Boolean(this.state.query)}
                        onClear={this.onSearchClear}
                    >
                        <input
                            id="search"
                            type="text"
                            value={this.state.query}
                            placeholder={LIST_OF_GROUPS_SEARCH_GROUPS_PLACEHOLDER}
                            onChange={this.onSearchChange}
                        />
                    </ClearableInput>

                    {isSuccess(categoriesLoadingState) && (
                        <ClearableInput
                            hasValue={Boolean(category)}
                            icon={
                                category && (
                                    <div onClick={onCategorySelectOpen}>
                                        <ImgCropped
                                            photo={category.coverPhoto}
                                            photoVersion={"s96x96"}
                                            width={24}
                                            height={24}
                                            mode={ImgCropMode.Cover}
                                            lazy={false}
                                        />
                                    </div>
                                )
                            }
                            className={styles.ListOfGroups__categoryFilter}
                            onClear={this.onCategoryClear}
                        >
                            {!category ? (
                                <div
                                    className={styles.ListOfGroups__categoryFilter__placeholder}
                                    onClick={onCategorySelectOpen}
                                >
                                    {CATEGORY}
                                    <span
                                        className={
                                            styles.ListOfGroups__categoryFilter__placeholder__arrow
                                        }
                                    >
                                        <FontAwesomeIcon icon={faAngleRight} />
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className={styles.ListOfGroups__categoryFilter__name}
                                    onClick={onCategorySelectOpen}
                                >
                                    {category.name}
                                </div>
                            )}
                        </ClearableInput>
                    )}
                </div>

                {query || category ? (
                    <SearchResults
                        loadingState={loadingState}
                        hasData={Boolean(groups.length)}
                        onCancelFilters={this.onCancelFilters}
                    >
                        <Interpolate
                            i18nKey={
                                query && category
                                    ? LIST_OF_GROUPS_SEARCH_RESULT_query_category_count
                                    : query
                                      ? LIST_OF_GROUPS_SEARCH_RESULT_query_count
                                      : LIST_OF_GROUPS_SEARCH_RESULT_category_count
                            }
                            count={count}
                            category={category && <strong>{category.name}</strong>}
                            query={query && <strong>{query}</strong>}
                        />
                    </SearchResults>
                ) : (
                    <>
                        <h2
                            className={cx(
                                styles.ListOfGroups__title,
                                styles.ListOfGroups__newestTitle,
                            )}
                        >
                            {LIST_OF_GROUPS_NEWEST}
                        </h2>
                        <GroupsGrid
                            askedForMembership={askedForMembership}
                            groups={newGroups}
                            joining={joining}
                            loadingState={loadingState}
                            location={location}
                            memberships={memberships}
                            isMobile={isMobile}
                            onJoinGroup={this.handleGroupJoin}
                            requestUser={requestUser}
                            newBadge
                        />

                        <h2
                            className={cx(
                                styles.ListOfGroups__title,
                                styles.ListOfGroups__popularTitle,
                            )}
                        >
                            {LIST_OF_GROUPS_POPULAR}
                        </h2>
                    </>
                )}

                <GroupsGrid
                    after={after}
                    askedForMembership={askedForMembership}
                    category={routeParams.categorySlug}
                    groups={groups}
                    hasNext={hasNext}
                    hasPrevious={hasPrevious}
                    joining={joining}
                    loadingState={loadingState}
                    location={location}
                    memberships={memberships}
                    isMobile={isMobile}
                    onJoinGroup={this.handleGroupJoin}
                    onLoad={this.handleLoad}
                    query={query}
                    requestUser={requestUser}
                    type="all"
                />
            </div>
        );
    }

    private handleFetchNew = () => {
        const { isMobile, newGroups, onFetchNew } = this.props;
        const limit = isMobile ? 2 : 6;

        const afterId = newGroups && newGroups.length > 0 ? -newGroups[newGroups.length - 1].id : 0;

        onFetchNew(limit, afterId);
    };

    private handleLoad = (groupType: string, category: string, query: string, after?: number) => {
        const { isMobile, onLoad } = this.props;
        const limitNew = isMobile ? 2 : 6;
        onLoad(category, query, limitNew, after);
    };

    private handleGroupJoin = (groupId) => {
        this.props.onJoinGroup(groupId, "list");
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: event.currentTarget.value }, this.searchRedirectDebounced);
    };

    private onSearchClear = (event: React.MouseEvent<HTMLAnchorElement>) => {
        this.setState({ query: "" }, this.searchRedirect);
    };

    private searchRedirect = () => {
        const {
            routeParams: { categorySlug },
        } = this.props;
        this.props.routeNavigate(
            {
                pathname: categorySlug
                    ? url(groupsListFromCategoryUrl, { categorySlug })
                    : groupsListUrl,
                search: this.state.query ? stringify({ query: this.state.query }) : undefined,
            },
            { replace: true },
        );
    };

    private onCategoryClear = (event: React.MouseEvent<HTMLAnchorElement>) => {
        const {
            location: { search },
        } = this.props;
        this.props.routeNavigate(
            {
                pathname: groupsListUrl,
                search,
            },
            { replace: true },
        );
    };

    private onCancelFilters = () => {
        this.setState({ query: "" });
        this.props.routeNavigate(groupsListUrl, { replace: true });
    };
}
